import { memo, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Headphones, Edit2, Trash2, MessageSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';

interface SupportMessageData {
  supportPhone?: string;
  supportMessage?: string;
}

export const SupportMessageNode = memo(({ data, id }: NodeProps<SupportMessageData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [supportPhone, setSupportPhone] = useState(data.supportPhone || '');
  const [supportMessage, setSupportMessage] = useState(data.supportMessage || '');

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSave = () => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              supportPhone,
              supportMessage,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  };

  const formatPhoneDisplay = (phone: string) => {
    if (!phone) return 'Não configurado';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Format as +XX (XX) XXXXX-XXXX
    if (digits.length === 13) {
      return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }
    return phone;
  };

  return (
    <>
      <div className="bg-orange-500/10 border-2 border-orange-500 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="!bg-orange-500" />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-orange-600">Mensagem de Apoio</div>
              <div className="text-xs text-gray-500">Notifica equipe de suporte</div>
            </div>
          </div>

          <div className="flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-orange-100 rounded transition-colors"
              title="Editar"
            >
              <Edit2 className="w-4 h-4 text-orange-600" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-orange-500" />
            <span className="font-medium text-gray-700">
              {formatPhoneDisplay(supportPhone)}
            </span>
          </div>

          {supportMessage && (
            <div className="mt-2 p-2 bg-white/50 rounded text-xs text-gray-600 italic line-clamp-2 border border-orange-200">
              {supportMessage}
            </div>
          )}

          {!supportPhone && (
            <div className="text-xs text-orange-600 italic">
              ⚠️ Configure o número de apoio
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="!bg-orange-500" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 text-brand-ivory max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Headphones className="w-5 h-5" />
              Configurar Mensagem de Apoio
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/60">
              Configure o número de apoio e a mensagem que será enviada
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-brand-ivory/80">
                Número de Telefone do Apoio <span className="text-red-400">*</span>
              </Label>
              <Input
                type="tel"
                value={supportPhone}
                onChange={(e) => setSupportPhone(e.target.value)}
                placeholder="Ex: 5511999999999"
                className="bg-brand-ink/50 border-brand-gold/20 text-brand-ivory mt-1"
              />
              <p className="text-xs text-brand-ivory/40 mt-1">
                Formato: código do país + DDD + número (somente números)
              </p>
            </div>

            <div>
              <Label className="text-brand-ivory/80">
                Mensagem <span className="text-red-400">*</span>
              </Label>
              <textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Digite a mensagem que será enviada para o apoio..."
                rows={6}
                className="w-full bg-brand-ink/50 border border-brand-gold/20 text-brand-ivory mt-1 resize-none rounded px-3 py-2"
              />
              <div className="mt-2 p-3 bg-brand-gold/5 rounded border border-brand-gold/20">
                <p className="text-xs text-brand-ivory/60 mb-2 font-medium">
                  Variáveis disponíveis:
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-brand-ivory/50">
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{client.name}}'}</code> - Nome do cliente</div>
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{client.phone}}'}</code> - Telefone do cliente</div>
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{client.email}}'}</code> - Email do cliente</div>
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{client.document}}'}</code> - Documento do cliente</div>
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{flow.name}}'}</code> - Nome do fluxo</div>
                  <div><code className="bg-brand-ink/50 px-1 rounded">{'{{execution.id}}'}</code> - ID da execução</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSave}
              disabled={!supportPhone || !supportMessage}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

SupportMessageNode.displayName = 'SupportMessageNode';
